import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { sendChatMessage, ChatHistoryMessage } from "api/chat.api";
import { useLanguage } from "locale/useLanguage";
import "./MisuChat.scss";

type VisibleMessage = {
  role: "user" | "assistant";
  content: string;
};

const MAX_HISTORY_PAIRS = 8;
const URL_REGEX = /(https?:\/\/[^\s]+)/g;
const CONTACT_URL = "https://www.noleggiosumisura.it/contatti/";

const SUGGESTION_KEYS = ["misu.suggestion_private", "misu.suggestion_formulas", "misu.suggestion_protection"] as const;

const ChatBubbleIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false">
    <path
      d="M7 5.5h10A3.5 3.5 0 0 1 20.5 9v6A3.5 3.5 0 0 1 17 18.5H9.2L5 21.2v-2.7A3.5 3.5 0 0 1 3.5 15V9A3.5 3.5 0 0 1 7 5.5Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
);

const renderPlainText = (text: string) => {
  const parts = text.split(URL_REGEX);
  return parts.map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      const href = part.replace(/[.,;:!?)]+$/, "");
      const trailing = part.slice(href.length);
      return (
        <React.Fragment key={index}>
          <a href={href} target="_blank" rel="noopener noreferrer">
            {href}
          </a>
          {trailing}
        </React.Fragment>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

type MisuChatProps = {
  visible: boolean;
};

const MisuChat: React.FC<MisuChatProps> = ({ visible }) => {
  const [t] = useLanguage();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<VisibleMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const fabRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        fabRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const send = useCallback(
    async (raw?: string) => {
      const message = (raw ?? draft).trim();
      if (!message || loading || disabled) {
        return;
      }

      const nextMessages: VisibleMessage[] = [...messages, { role: "user", content: message }];
      setMessages(nextMessages);
      setDraft("");
      setLoading(true);

      const history: ChatHistoryMessage[] = nextMessages
        .slice(0, -1)
        .slice(-MAX_HISTORY_PAIRS * 2)
        .map((item) => ({ role: item.role, content: item.content }));

      try {
        const response = await sendChatMessage(message, history);
        setDisabled(response.disabled);
        setMessages((current) => [...current, { role: "assistant", content: response.reply }]);
      } catch {
        setMessages((current) => [...current, { role: "assistant", content: t("misu.error") }]);
      } finally {
        setLoading(false);
      }
    },
    [disabled, draft, loading, messages, t]
  );

  if (!visible) {
    return null;
  }

  const showWelcome = messages.length === 0 && !loading;

  return (
    <div className={`misu-chat${open ? " misu-chat--open" : ""}`}>
      {open && (
        <section
          className="misu-chat-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="misu-chat-title"
        >
          <header className="misu-chat-header">
            <h2 id="misu-chat-title" className="misu-chat-title">
              {t("misu.name")}
            </h2>
            <p className="misu-chat-subtitle">{t("misu.subtitle")}</p>
          </header>

          <div className="misu-chat-messages" ref={listRef}>
            {showWelcome && (
              <>
                <div className="misu-chat-bubble misu-chat-bubble--assistant">
                  <p>{t("misu.welcome")}</p>
                </div>
                <div className="misu-chat-suggestions" role="group" aria-label={t("misu.suggestions_label")}>
                  {SUGGESTION_KEYS.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className="misu-chat-chip"
                      onClick={() => send(t(key))}
                    >
                      {t(key)}
                    </button>
                  ))}
                </div>
              </>
            )}
            {messages.map((item, index) => (
              <div
                key={`${item.role}-${index}`}
                className={`misu-chat-bubble misu-chat-bubble--${item.role}`}
              >
                <p>{renderPlainText(item.content)}</p>
              </div>
            ))}
            {loading && (
              <div className="misu-chat-bubble misu-chat-bubble--assistant misu-chat-bubble--typing">
                <CircularProgress size={16} thickness={5} />
              </div>
            )}
          </div>

          <footer className="misu-chat-footer">
            <form
              className="misu-chat-composer"
              onSubmit={(event) => {
                event.preventDefault();
                send();
              }}
            >
              <input
                ref={inputRef}
                className="misu-chat-input"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t("misu.placeholder")}
                disabled={disabled || loading}
                maxLength={500}
                aria-label={t("misu.placeholder")}
              />
              <button
                type="submit"
                className="misu-chat-send"
                aria-label={t("misu.send")}
                disabled={disabled || loading || !draft.trim()}
              >
                <ArrowForwardIcon fontSize="small" />
              </button>
            </form>
            <a
              className="misu-chat-contact"
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("misu.contact")}
            </a>
            <p className="misu-chat-disclaimer">{t("misu.disclaimer")}</p>
          </footer>
        </section>
      )}

      <IconButton
        ref={fabRef}
        className="misu-chat-fab"
        aria-label={open ? t("misu.close") : t("misu.open")}
        onClick={() => setOpen((current) => !current)}
        size="large"
      >
        {open ? <CloseIcon /> : <ChatBubbleIcon />}
      </IconButton>
    </div>
  );
};

export default MisuChat;
